import { getCustomerStatusLabel } from "./peopleHelpers";
import { isAdminRole } from "./peoplePermissions";

export function mapApiCustomerStatus(customer) {
  if (customer?.banned_until) return "blocked";
  const s = (customer?.people_status || customer?.status || "").toLowerCase();
  if (s === "waiting_reply" || s === "waitingreply") return "waitingReply";
  if (s === "inactive") return "inactive";
  if (s === "blocked" || s === "banned") return "blocked";
  return "active";
}

export function formatCustomerLastActivity(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// for customer last seen date format dd/mm/yyyy
export function formatCustomerLastSeenDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function mapApiCustomerToTableRow(customer) {
  const groups = customer.groups || [];
  const groupIds = groups.map((g) => g.id || g._id).filter(Boolean);

  return {
    id: customer.id,
    name: customer.name || "",
    email: customer.primary_email || "",
    phone: customer.primary_phone || "—",
    group: customer.groups_display || customer.primary_group?.name || "—",
    groupIds,
    status: mapApiCustomerStatus(customer),
    chats: customer.chat_count ?? customer.conversations_count ?? 0,
    lastActivity: formatCustomerLastActivity(customer.last_seen),
    avatar: customer.profile_picture || null,
    company: customer.company || "",
    tags: customer.tags || [],
    isGuest: customer.is_guest ?? false,
    raw: customer,
  };
}

export function mapApiCustomerToDetailView(customer) {
  if (!customer) return null;

  const groups = (customer.groups || []).map((g) => g.name || g.title).filter(Boolean);
  const locationParts = [
    customer.address?.city,
    customer.address?.state,
    customer.location?.city,
    customer.address?.country,
  ].filter(Boolean);

  return {
    id: customer.id,
    name: customer.name || "—",
    email: customer.primary_email || "—",
    phone: customer.primary_phone || "—",
    company: customer.company || "—",
    location: locationParts.length ? locationParts.join(", ") : customer.location?.city || "—",
    timezone: customer.timezone || "—",
    lastSeen: formatCustomerLastActivity(customer.last_seen),
    group: customer.groups_display || customer.primary_group?.name || "—",
    groupLabel: customer.groups_display || customer.primary_group?.name || "—",
    groupIds: (customer.groups || []).map((g) => g.id || g._id).filter(Boolean),
    status: mapApiCustomerStatus(customer),
    statusLabel: getCustomerStatusLabel(mapApiCustomerStatus(customer)),
    chats: customer.chat_count ?? customer.conversations_count ?? 0,
    tickets: customer.ticket_count ?? 0,
    tags: customer.tags?.length ? customer.tags : [],
    avatar: customer.profile_picture || null,
    websites: customer.websites || [],
    emails: customer.emails || [],
    phones: customer.phones || [],
    address: customer.address || {},
    deviceType: customer.device_type || "—",
    history: [],
    raw: customer,
    lastSeenDate: formatCustomerLastSeenDate(customer.last_seen),
  };
}



export function isCustomerBanAdminRole(roleName) {
  return isAdminRole(roleName);
}

/** Ban duration choices shown in CustomerBanModal (no permanent). */
export function getCustomerBanDurationOptions(roleName) {
  if (isCustomerBanAdminRole(roleName)) {
    return [
      { value: "30", label: "30 days" },
      { value: "60", label: "60 days" },
      { value: "90", label: "90 days" },
    ];
  }
  return [{ value: "7", label: "7 days" }];
}

export function getDefaultCustomerBanDuration(roleName) {
  return getCustomerBanDurationOptions(roleName)[0]?.value ?? "7";
}

/**
 * @param {string|number} duration
 * @param {{ fromLiveChat?: boolean, userId?: string|null, roomId?: string|null }} [options]
 */
export function buildCustomerBanPayload(duration = "7", options = {}) {
  const { fromLiveChat = false, userId = null, roomId = null } = options;
  const body = {
    duration_days: Number(duration) || 7,
  };
  if (fromLiveChat) {
    body.from_live_chat = true;
    if (userId) body.user_id = String(userId);
    if (roomId) body.room_id = String(roomId);
  }
  return body;
}

export function mapCustomerStatusToApi(status) {
  if (status === "waitingReply") return "waiting_reply";
  if (status === "blocked") return "banned";
  return status;
}

export function buildCustomerUpdatePayload(form, { includeStatus = false } = {}) {
  const payload = {
    name: form.name?.trim(),
    email: form.email?.trim(),
    company: form.company?.trim() || "",
    group_ids: form.groupIds || [],
    tags: form.tags?.length ? form.tags : [],
    address: {
      city: form.city?.trim() || "",
      country: form.country?.trim() || "",
    },
  };

  if (form.phone?.trim()) {
    payload.phones = [{ number: form.phone.trim(), is_primary: true }];
  } else {
    payload.phones = [];
  }

  if (form.website?.trim()) {
    payload.websites = [form.website.trim()];
  } else if (form.websites?.length) {
    payload.websites = form.websites;
  } else {
    payload.websites = [];
  }

  if (
    includeStatus &&
    (form.status === "active" || form.status === "inactive")
  ) {
    payload.people_status = mapCustomerStatusToApi(form.status);
  }

  return payload;
}

export function buildCustomerCreatePayload(form) {
  const payload = {
    name: form.name?.trim(),
    email: form.email?.trim(),
    company: form.company?.trim() || undefined,
    group_ids: form.groupId ? [form.groupId] : [],
    tags: form.tags?.length ? form.tags : [],
    address: {},
  };

  if (form.phone?.trim()) {
    payload.phones = [{ number: form.phone.trim(), is_primary: true }];
  }
  if (form.website?.trim()) payload.websites = [form.website.trim()];

  return payload;
}

/** POST /customer/bulk body builder. */
export const CUSTOMER_BULK_ACTIONS = [
  "assign_groups",
  "add_groups",
  "remove_groups",
  "change_status",
  "add_tags",
  "ban",
  "unban",
  "delete",
];

export function buildCustomerBulkBody(action, customerIds, extra = {}) {
  if (!CUSTOMER_BULK_ACTIONS.includes(action)) {
    throw new Error(`Unsupported customer bulk action: ${action}`);
  }

  return {
    action,
    customer_ids: customerIds,
    ...extra,
  };
}

export function tabToCustomerListStatus(tab) {
  if (!tab || tab === "all") return "all";
  if (tab === "blocked") return "banned";
  if (tab === "waitingReply") return "waiting_reply";
  return tab;
}

/** Merge `counts` from GET /customer/list into Redux tabTotals keys. */
export function applyApiCustomerCounts(tabTotals = {}, counts) {
  if (!counts || typeof counts !== "object") return tabTotals;

  const next = { ...tabTotals };
  if (counts.all != null) next.all = counts.all;
  if (counts.active != null) next.active = counts.active;
  if (counts.inactive != null) next.inactive = counts.inactive;
  if (counts.banned != null) next.banned = counts.banned;
  if (counts.waiting_reply != null) next.waiting_reply = counts.waiting_reply;
  if (counts.waitingReply != null) next.waiting_reply = counts.waitingReply;
  return next;
}

export function computeCustomerTabCounts(tabTotals = {}) {
  return {
    all: tabTotals.all ?? 0,
    active: tabTotals.active ?? 0,
    waitingReply: tabTotals.waiting_reply ?? tabTotals.waitingReply ?? 0,
    inactive: tabTotals.inactive ?? 0,
    blocked: tabTotals.banned ?? tabTotals.blocked ?? 0,
  };
}
